import React, { useState, useEffect, useCallback } from 'react';
import {
  leaveSlotService,
  LeaveSlotCreate,
  LeaveSlotBulkCreate,
  AffectedAppointment,
} from '../../../services/availability/leaveSlotService';
import { DoctorPractice } from '../../../services/profile/profileService';
import SelectiveAppointmentHandlingView from '../SelectiveAppointmentHandling/SelectiveAppointmentHandling';
import './AddLeaveForm.css';

interface AddLeaveFormProps {
  selectedDate: Date;
  practices: DoctorPractice[];
  onClose: () => void;
  onSuccess: () => void;
}

type LeaveType = 'holiday' | 'sick_leave' | 'emergency' | 'other';
type LeaveScope = 'all_practices' | 'single_practice';
type TimePreset = 'full_day' | 'morning' | 'afternoon' | 'evening' | 'custom';

const AddLeaveForm: React.FC<AddLeaveFormProps> = ({
  selectedDate,
  practices,
  onClose,
  onSuccess,
}) => {
  const [selectedPracticeIds, setSelectedPracticeIds] = useState<Set<number>>(new Set());
  const [leaveDate, setLeaveDate] = useState<Date>(selectedDate);
  const [endDate, setEndDate] = useState<Date>(() => {
    const nextDay = new Date(selectedDate);
    nextDay.setDate(nextDay.getDate() + 1);
    return nextDay;
  });
  const [startTime, setStartTime] = useState<string>('00:00');
  const [endTime, setEndTime] = useState<string>('23:59');
  const [selectedLeaveType, setSelectedLeaveType] = useState<LeaveType>('holiday');
  const [reason, setReason] = useState<string>('');
  const [leaveScope, setLeaveScope] = useState<LeaveScope>('all_practices');
  const [autoCancelAppointments, setAutoCancelAppointments] = useState<boolean>(false);
  const [isBulkMode, setIsBulkMode] = useState<boolean>(false);
  const [selectedTimePreset, setSelectedTimePreset] = useState<TimePreset>('full_day');
  const [timeValidationError, setTimeValidationError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showingConflictDialog, setShowingConflictDialog] = useState<boolean>(false);
  const [conflictingAppointments, setConflictingAppointments] = useState<AffectedAppointment[]>([]);
  const [showingSelectiveHandling, setShowingSelectiveHandling] = useState<boolean>(false);

  useEffect(() => {
    // Auto-select all practices when practices are loaded
    if (practices.length > 0 && leaveScope === 'all_practices') {
      const allPracticeIds = new Set(practices.filter((p) => p.id).map((p) => p.id!));
      setSelectedPracticeIds(allPracticeIds);
    }
  }, [practices, leaveScope]);

  const validateTimeRange = useCallback(() => {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;

    if (endMinutes <= startMinutes) {
      setTimeValidationError('End time must be after start time');
    } else {
      setTimeValidationError(null);
    }
  }, [startTime, endTime]);

  useEffect(() => {
    validateTimeRange();
  }, [validateTimeRange]);

  const handleStartTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartTime(e.target.value);
    validateTimeRange();
  };

  const handleEndTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndTime(e.target.value);
    validateTimeRange();
  };

  const formatDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isFormValid = (): boolean => {
    if (leaveScope === 'single_practice' && selectedPracticeIds.size === 0) {
      return false;
    }
    if (isBulkMode && endDate < leaveDate) {
      return false;
    }
    if (timeValidationError) {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (leaveDate < today) {
      return false;
    }
    return true;
  };

  const handlePracticeToggle = (practiceId: number) => {
    const newSet = new Set(selectedPracticeIds);
    if (newSet.has(practiceId)) {
      newSet.delete(practiceId);
    } else {
      newSet.add(practiceId);
    }
    setSelectedPracticeIds(newSet);
  };

  const createLeaveSlot = async () => {
    if (!isFormValid()) {
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const practiceId =
        leaveScope === 'all_practices'
          ? practices[0]?.id
          : Array.from(selectedPracticeIds)[0];

      if (!practiceId) {
        setError('No practice available. Please add a practice first.');
        setIsCreating(false);
        return;
      }

      const leaveSlot: LeaveSlotCreate = {
        practice_id: leaveScope === 'all_practices' ? null : practiceId,
        leave_date: formatDateString(leaveDate),
        start_time: startTime,
        end_time: endTime,
        leave_type: selectedLeaveType,
        reason: reason || null,
        leave_scope: leaveScope,
        auto_cancel_appointments: autoCancelAppointments,
      };

      // Create leave slot - backend will check for conflicts and return them in response
      const created = await leaveSlotService.createLeaveSlot(practiceId, leaveSlot);
      
      // Check if response has affected appointments (conflicts detected during creation)
      if (created.affected_appointments && created.affected_appointments.length > 0 && !autoCancelAppointments) {
        setConflictingAppointments(created.affected_appointments);
        setShowingConflictDialog(true);
        setIsCreating(false);
        return;
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to create leave slot');
    } finally {
      setIsCreating(false);
    }
  };

  const createLeaveWithAutoCancel = async () => {
    if (!isFormValid()) {
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const practiceId =
        leaveScope === 'all_practices'
          ? practices[0]?.id
          : Array.from(selectedPracticeIds)[0];

      if (!practiceId) {
        setError('No practice available. Please add a practice first.');
        setIsCreating(false);
        return;
      }

      const leaveSlot: LeaveSlotCreate = {
        practice_id: leaveScope === 'all_practices' ? null : practiceId,
        leave_date: formatDateString(leaveDate),
        start_time: startTime,
        end_time: endTime,
        leave_type: selectedLeaveType,
        reason: reason || null,
        leave_scope: leaveScope,
        auto_cancel_appointments: true,
      };

      await leaveSlotService.createLeaveSlot(practiceId, leaveSlot);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to create leave slot');
    } finally {
      setIsCreating(false);
    }
  };

  const createBulkLeave = async () => {
    if (!isFormValid()) {
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const practiceId =
        leaveScope === 'all_practices'
          ? practices[0]?.id
          : Array.from(selectedPracticeIds)[0];

      if (!practiceId) {
        setError('No practice available. Please add a practice first.');
        setIsCreating(false);
        return;
      }

      const bulkCreate: LeaveSlotBulkCreate = {
        start_date: formatDateString(leaveDate),
        end_date: formatDateString(endDate),
        start_time: startTime,
        end_time: endTime,
        leave_type: selectedLeaveType,
        reason: reason || null,
        leave_scope: leaveScope,
        practice_id: leaveScope === 'all_practices' ? null : practiceId,
        auto_cancel_appointments: false,
      };

      await leaveSlotService.bulkCreateLeaveSlots(practiceId, bulkCreate);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to create leave slots');
    } finally {
      setIsCreating(false);
    }
  };

  const handleSubmit = async () => {
    if (isBulkMode) {
      await createBulkLeave();
    } else {
      await createLeaveSlot();
    }
  };

  const getPracticeIdForHandling = (): number | null => {
    return leaveScope === 'all_practices'
      ? practices[0]?.id || null
      : Array.from(selectedPracticeIds)[0] || null;
  };

  const selectedPracticesLabel = (): string => {
    if (selectedPracticeIds.size === 0) {
      return 'Select practices';
    }
    const selectedNames = practices
      .filter((p) => p.id && selectedPracticeIds.has(p.id))
      .map((p) => p.practice_name);
    if (selectedNames.length === 1) {
      return selectedNames[0];
    }
    return `${selectedNames.length} practices selected`;
  };

  const daysDifference = (): number => {
    const diffTime = endDate.getTime() - leaveDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays + 1;
  };

  return (
    <div className="add-leave-form">
      <div className="add-leave-form-header">
        <h2>Add Leave</h2>
        <button type="button" className="add-leave-form-close" onClick={onClose}>
          ×
        </button>
      </div>

      <div className="add-leave-form-body">
        {error && <div className="add-leave-form-error">{error}</div>}

        {/* Practice Selection */}
        {practices.length > 0 && (
          <div className="add-leave-form-section">
            <label className="add-leave-form-label">Practice</label>
            <div className="add-leave-form-practice-toggle">
              <button
                type="button"
                className={`practice-toggle-button ${leaveScope === 'all_practices' ? 'active' : ''}`}
                onClick={() => setLeaveScope('all_practices')}
              >
                All Practices
              </button>
              <button
                type="button"
                className={`practice-toggle-button ${leaveScope === 'single_practice' ? 'active' : ''}`}
                onClick={() => {
                  setLeaveScope('single_practice');
                  if (selectedPracticeIds.size === 0) {
                    const allPracticeIds = new Set(
                      practices.filter((p) => p.id).map((p) => p.id!)
                    );
                    setSelectedPracticeIds(allPracticeIds);
                  }
                }}
              >
                Select Practices
              </button>
            </div>
            {leaveScope === 'single_practice' && (
              <div className="add-leave-form-practice-select">
                <div className="practice-checkbox-list">
                  {practices
                    .filter((p) => p.id)
                    .map((practice) => (
                      <label key={practice.id} className="practice-checkbox-item">
                        <input
                          type="checkbox"
                          checked={selectedPracticeIds.has(practice.id!)}
                          onChange={() => handlePracticeToggle(practice.id!)}
                        />
                        <span>{practice.practice_name}</span>
                      </label>
                    ))}
                </div>
                <div className="practice-selected-label">{selectedPracticesLabel()}</div>
              </div>
            )}
          </div>
        )}

        {/* Multiple Days Toggle */}
        <div className="add-leave-form-section">
          <label className="add-leave-form-label">Options</label>
          <div className="add-leave-form-toggle">
            <label>
              <input
                type="checkbox"
                checked={isBulkMode}
                onChange={(e) => setIsBulkMode(e.target.checked)}
              />
              <span>Create for multiple days</span>
            </label>
          </div>
        </div>

        {/* Date Selection */}
        <div className="add-leave-form-section">
          <label className="add-leave-form-label">{isBulkMode ? 'Date Range' : 'Date'}</label>
          {isBulkMode ? (
            <>
              <input
                type="date"
                value={formatDateString(leaveDate)}
                min={formatDateString(new Date())}
                onChange={(e) => setLeaveDate(new Date(e.target.value))}
                className="add-leave-form-input"
              />
              <input
                type="date"
                value={formatDateString(endDate)}
                min={formatDateString(leaveDate)}
                onChange={(e) => setEndDate(new Date(e.target.value))}
                className="add-leave-form-input"
              />
              <div className="add-leave-form-info">
                Will create leave for {daysDifference()} day(s)
              </div>
            </>
          ) : (
            <input
              type="date"
              value={formatDateString(leaveDate)}
              min={formatDateString(new Date())}
              onChange={(e) => setLeaveDate(new Date(e.target.value))}
              className="add-leave-form-input"
            />
          )}
        </div>

        {/* Time Selection */}
        <div className="add-leave-form-section">
          <label className="add-leave-form-label">Time</label>
          <select
            value={selectedTimePreset}
            onChange={(e) => setSelectedTimePreset(e.target.value as TimePreset)}
            className="add-leave-form-select"
          >
            <option value="full_day">Full Day</option>
            <option value="morning">Morning</option>
            <option value="afternoon">Afternoon</option>
            <option value="evening">Evening</option>
            <option value="custom">Custom</option>
          </select>
          <input
            type="time"
            value={startTime}
            onChange={handleStartTimeChange}
            className="add-leave-form-input"
          />
          <input
            type="time"
            value={endTime}
            onChange={handleEndTimeChange}
            className="add-leave-form-input"
          />
          {timeValidationError && (
            <div className="add-leave-form-error-text">{timeValidationError}</div>
          )}
        </div>

        {/* Leave Type */}
        <div className="add-leave-form-section">
          <label className="add-leave-form-label">Leave Type</label>
          <select
            value={selectedLeaveType}
            onChange={(e) => setSelectedLeaveType(e.target.value as LeaveType)}
            className="add-leave-form-select"
          >
            <option value="holiday">Holiday</option>
            <option value="sick_leave">Sick Leave</option>
            <option value="emergency">Emergency</option>
            <option value="other">Other</option>
          </select>
          <input
            type="text"
            placeholder="Reason (Optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="add-leave-form-input"
          />
        </div>

        {/* Auto-cancel Toggle */}
        {!isBulkMode && (
          <div className="add-leave-form-section">
            <label className="add-leave-form-label">Options</label>
            <div className="add-leave-form-toggle">
              <label>
                <input
                  type="checkbox"
                  checked={autoCancelAppointments}
                  onChange={(e) => setAutoCancelAppointments(e.target.checked)}
                />
                <span>Auto-cancel Appointments</span>
              </label>
            </div>
          </div>
        )}
      </div>

      <div className="add-leave-form-footer">
        <button type="button" className="add-leave-form-cancel" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="add-leave-form-save"
          onClick={handleSubmit}
          disabled={isCreating || !isFormValid()}
        >
          {isCreating ? 'Creating...' : isBulkMode ? 'Create Multiple Days' : 'Save'}
        </button>
      </div>

      {/* Conflict Dialog */}
      {showingConflictDialog && (
        <div className="modal-overlay" onClick={() => setShowingConflictDialog(false)}>
          <div className="modal-content-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Conflicts Detected</h3>
            </div>
            <div className="modal-body">
              <p>
                You have {conflictingAppointments.length} conflicting appointment(s) in this time
                slot.
              </p>
              <p>These appointments will be affected if you proceed. How would you like to handle them?</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="modal-button-secondary"
                onClick={() => setShowingConflictDialog(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="modal-button-primary"
                onClick={async () => {
                  setShowingConflictDialog(false);
                  await createLeaveWithAutoCancel();
                }}
              >
                Auto-cancel Appointments
              </button>
              <button
                type="button"
                className="modal-button-primary"
                onClick={() => {
                  setShowingConflictDialog(false);
                  setShowingSelectiveHandling(true);
                }}
              >
                Handle Manually
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Selective Appointment Handling */}
      {showingSelectiveHandling && getPracticeIdForHandling() && (
        <div className="modal-overlay" onClick={() => setShowingSelectiveHandling(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <SelectiveAppointmentHandlingView
              practiceId={getPracticeIdForHandling()!}
              leaveSlot={{
                practice_id: leaveScope === 'all_practices' ? null : getPracticeIdForHandling(),
                leave_date: formatDateString(leaveDate),
                start_time: startTime,
                end_time: endTime,
                leave_type: selectedLeaveType,
                reason: reason || null,
                leave_scope: leaveScope,
                auto_cancel_appointments: false,
              }}
              conflictingAppointments={conflictingAppointments}
              onClose={() => setShowingSelectiveHandling(false)}
              onSuccess={() => {
                onSuccess();
                onClose();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AddLeaveForm;

